import { adminFetch, ApiError } from "./userApi";

export interface UserProject {
  project_id:    string;
  name:          string;
  clone_status:  "cloning" | "active" | "clone_failed" | "removed" | string;
  github_url?:   string;
  created_at:    string;
  clone_error?:  string;
  clone_pct?:    number;
  clone_phase?:  string;
  clone_message?: string;
  // #3052 — extended from {owner|member} to {owner|member|admin|invited}
  // so the /hives list chip can render the cross-tenant admin view (#1447)
  // and pending-invite states. Backend stamps "admin" only for fleet-admin
  // JWTs viewing a row whose linux_username doesn't match the caller's.
  caller_role?:  "owner" | "member" | "admin" | "invited";
  // #2147 — webhook fan-out sync state stamped by backend handle_webhook_github
  // on every push to main/master. Empty until the first push lands. Status
  // sequence: '' → 'pending_pull' (job dispatched) → 'synced' (ack ok) | 'diverged'.
  last_synced_at?:  string;
  last_synced_sha?: string;
  last_synced_by?:  string;
  last_synced_ref?: string;
  sync_status?:     "" | "pending_pull" | "synced" | "diverged" | string;
  // #1997 — free-text description set at create time (wizard step 1a). Backend
  // returns "" when the user didn't supply one. Persisted on 2hives-projects.
  description?:     string;
}

// Admin all-hives row: same as UserProject but with platform-admin extras
// (owner_email + org + template). Returned by GET /admin/hives?scope=all (#1448).
// Used by AdminAllHives.tsx page (#1447). owner_email is the email resolved by
// the backend from cognito_sub -> 2hives-users batch_get.
export interface AdminHiveRow {
  name:         string;
  status:       string;
  github_url?:  string;
  role:         "owner" | "collaborator";
  owner_sub:    string;
  owner_email:  string;
  org:          string;
  template:     string;
  created_at:   string;
  tier_locked?: boolean;
  is_active_slot?: boolean;
  last_synced_at?:  string;
  last_synced_sha?: string;
  last_synced_by?:  string;
  last_synced_ref?: string;
  sync_status?:     string;
  description?:     string;
}

export interface SyncOrphansResult {
  linked:           number;
  already_linked:   number;
  skipped_non_hive: number;
  owner_sub:        string;
  org:              string;
  repos:            Array<{ slug: string; status: string }>;
}

export interface ProjectMember {
  user_id:  string;
  email:    string;
  role:     "owner" | "member";
  status:   "accepted" | "pending" | "removed";
}

export interface ProjectDirectory {
  path:          string;
  display_name:  string;
  readme_lines?: number;
}

export interface ProjectAgent {
  id:            string;
  name:          string;
  dir:           string;
  // #2635 — keep the union open so quarantined/down/alive from the supervisor
  // heartbeat reach the UI verbatim; AgentDot/statusLabel decide how to render.
  status:        "running" | "stopped" | "starting" | "restarting" | "error" | "quarantined" | string;
  model?:        string;
  role?:         string;
  last_activity?: string;
  // #2059: card the agent is currently working on. null/undefined when idle.
  // Backend resolves the title in the same response so the chip can render
  // `working on: #<id> [<title>] →` without a per-row roundtrip.
  current_card_id?:    number | null;
  current_card_title?: string;
  // #2575: master agent contract — every hive has exactly one auto-provisioned
  // master, undeletable + unstoppable, runs 24/7. `protected` is the broader
  // "no destructive ops" flag (master agents always have protected=true; future
  // operator-pinned agents may set protected=true without being master).
  // Both default to false / undefined for agents written before #2575.
  is_master?:  boolean;
  protected?:  boolean;
  // #2597 (fleet PR #48) / #2599 (frontend tree render) — self-scaling agent
  // tree topology. Roots (master, manual) omit parent_agent_id and carry
  // spawn_depth=0; children point at the parent agent_id and carry
  // spawn_depth = parent.spawn_depth + 1 (capped at 6, supervisor spec §6).
  // Older rows (pre-#2595) lack both — UI treats them as roots.
  parent_agent_id?: string | null;
  spawn_depth?:     number;
  // #3151 — per-agent public|private flag. Rows written before #3151
  // lack the attribute → safe default 'private' (UI shows toggle OFF).
  // 'public' exposes the agent to the public WS API (#3099 sub-cards).
  visibility?: "public" | "private";
  // #3155 / #3160 — canonical platform agent_role enum string. See
  // backend `functions/fleet/_agent_roles.py` for the registry.
  // Values shipped so far:
  //   * "cost_optimization_master" (#3155 / #3123)
  //   * "support_master"           (#3101 / #3096)
  // Reserved-words hygiene (#3154): we use the canonical `agent_role`
  // name (NOT `role`, which is a DDB reserved word and also serves the
  // legacy free-text descriptor populated by add-agent flows). Legacy
  // rows that lack the attribute → undefined; UI surfaces (the cost-opt
  // sidebar chip in #3160) treat undefined as "not a registered role".
  agent_role?: string;
  // #3197 — per-agent MCP tool allowlist (list of fnmatch globs). Rows
  // written before #3197 lack the attribute → safe default ['*'] (full
  // tool access, backward-compatible). Companion to `visibility`:
  // visibility decides who can reach the agent, allowed_tools decides
  // what the agent can do once reached. The runtime gate lives in the
  // fleet runtime (_fleet/scripts/mcp_tool_allowlist.py); this column
  // is the single source of truth read by both layers.
  allowed_tools?: string[];
}

// #2635 — Map a DDB row (or already-shaped ProjectAgent) into the frontend
// contract. Backend writes `agent_id`/`directory` + supervisor status vocab
// (`alive`/`down`/`quarantined`/`starting`/…) whereas the UI was built
// against `id`/`dir` + `running`/`stopped`/`starting`. We do the translation
// in one place so callers stay typed and a later contract alignment on the
// backend is a one-line revert here, not a sweep.
export function normalizeAgent(raw: Record<string, unknown>): ProjectAgent {
  const id          = String(raw.id ?? raw.agent_id ?? "");
  const dir         = String(raw.dir ?? raw.directory ?? "");
  const rawStatus   = String(raw.status ?? "stopped");
  // Map supervisor heartbeat status → UI status. Anything we don't recognise
  // (including "quarantined") passes through so the dot/label can show it.
  const status =
    rawStatus === "alive" ? "running" :
    rawStatus === "down"  ? "stopped" :
    rawStatus;
  const ccid = raw.current_card_id;
  // #2591 — master agent flags. Backend may write them as actual booleans, as
  // DDB-typed wrappers (`{BOOL: true}`), or omit them entirely on pre-#2575
  // rows. Coerce loosely so the UI surface treats any truthy value as the flag,
  // and a missing attribute as `false` — never `undefined` masquerading as
  // "maybe master" in a downstream conditional.
  const isMaster  = raw.is_master === true || (raw.is_master as { BOOL?: boolean } | undefined)?.BOOL === true;
  const protectedFlag = raw.protected === true || (raw.protected as { BOOL?: boolean } | undefined)?.BOOL === true;
  // #2599 — parent_agent_id + spawn_depth plumbing. DDB sends either bare
  // values or {S: "..."} / {N: "1"} wrappers depending on the marshaller; we
  // accept both. Empty string parent → null (root). Missing depth → undefined
  // (the tree builder will treat undefined as 0).
  const rawParent = raw.parent_agent_id;
  let parent: string | null | undefined;
  if (rawParent == null) parent = undefined;
  else if (typeof rawParent === "string") parent = rawParent === "" ? null : rawParent;
  else {
    const sWrap = (rawParent as { S?: string }).S;
    parent = sWrap ? sWrap : null;
  }
  const rawDepth = raw.spawn_depth;
  let depth: number | undefined;
  if (rawDepth == null) depth = undefined;
  else if (typeof rawDepth === "number") depth = rawDepth;
  else if (typeof rawDepth === "string") { const n = Number(rawDepth); depth = Number.isFinite(n) ? n : undefined; }
  else {
    const nWrap = (rawDepth as { N?: string }).N;
    if (nWrap != null) { const n = Number(nWrap); depth = Number.isFinite(n) ? n : undefined; }
  }
  // #3151 — visibility: tolerate the bare string + the DDB-typed wrapper.
  // Missing / unrecognised → 'private' so the UI toggle defaults OFF.
  const rawVisibility = raw.visibility;
  let visibility: "public" | "private" = "private";
  if (typeof rawVisibility === "string") {
    visibility = rawVisibility.trim().toLowerCase() === "public" ? "public" : "private";
  } else if (rawVisibility && typeof rawVisibility === "object") {
    const sWrap = (rawVisibility as { S?: string }).S;
    if (typeof sWrap === "string" && sWrap.trim().toLowerCase() === "public") {
      visibility = "public";
    }
  }
  // #3155 / #3160 — canonical platform agent_role enum. Tolerate the bare
  // string + the DDB-typed {S: "..."} wrapper. Empty string / missing /
  // unrecognised → undefined so consumers can treat "not set" cleanly.
  const rawAgentRole = raw.agent_role;
  let agentRole: string | undefined;
  if (typeof rawAgentRole === "string") {
    const trimmed = rawAgentRole.trim();
    agentRole = trimmed === "" ? undefined : trimmed;
  } else if (rawAgentRole && typeof rawAgentRole === "object") {
    const sWrap = (rawAgentRole as { S?: string }).S;
    if (typeof sWrap === "string" && sWrap.trim() !== "") agentRole = sWrap.trim();
  }
  return {
    id,
    name:               String(raw.name ?? id),
    dir,
    status,
    model:              raw.model              as string | undefined,
    role:               raw.role               as string | undefined,
    last_activity:      raw.last_activity      as string | undefined,
    current_card_id:    typeof ccid === "number" ? ccid : (ccid == null ? null : Number(ccid) || null),
    current_card_title: raw.current_card_title as string | undefined,
    is_master:          isMaster,
    protected:          protectedFlag,
    parent_agent_id:    parent,
    spawn_depth:        depth,
    visibility,
    agent_role:         agentRole,
    allowed_tools:      coerceAllowedTools(raw.allowed_tools),
  };
}

// #3197 — allowed_tools coercion. Accept bare list[str] (Resource API),
// {"L":[{"S":"..."}]} (low-level marshalled), bare string, or missing →
// undefined here so the UI distinguishes "row never touched" from
// "explicitly set to ['*']". Both render the same in the panel, but the
// audit row carries old_list correctly.
function coerceAllowedTools(raw: unknown): string[] | undefined {
  if (raw == null) return undefined;
  if (Array.isArray(raw)) {
    const collected = raw
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
          const s = (item as { S?: string }).S;
          return typeof s === "string" ? s : null;
        }
        return null;
      })
      .filter((s): s is string => typeof s === "string" && s.length > 0);
    return collected.length > 0 ? collected : ["*"];
  }
  if (typeof raw === "object") {
    const inner = (raw as { L?: unknown }).L;
    if (Array.isArray(inner)) return coerceAllowedTools(inner);
    return undefined;
  }
  if (typeof raw === "string" && raw) return [raw];
  return undefined;
}

export function normalizeAgentList(
  r: ProjectAgent[] | { agents?: ProjectAgent[] } | Array<Record<string, unknown>> | null | undefined,
): ProjectAgent[] {
  if (!r) return [];
  const list = Array.isArray(r) ? r : (r.agents ?? []);
  return (list as Array<Record<string, unknown>>).map(normalizeAgent);
}

export interface ProjectDeleteOptions {
  keep_github?: boolean;
}

// ── Filesystem (#1645/#1646) ──────────────────────────────────────────────────
export interface FsEntry {
  name: string;
  type: "file" | "dir";
  size?: number;
}

// files GET response (#1645): box verb result JSON — {content, size, truncated};
// binary/oversize flagged. path/type may be echoed back but aren't relied on.
export interface HiveFile {
  path?:     string;
  type?:     "file";
  size?:     number;
  content?:  string;   // omitted/empty when binary
  binary?:   boolean;  // true when the file is binary
  truncated?: boolean; // true when content was cut at the read cap (1MB default / 5MB)
}

export const projectsApi = {
  list: (): Promise<UserProject[]> =>
    adminFetch("GET", "/admin/hives"),

  get: (name: string): Promise<UserProject> =>
    adminFetch("GET", `/admin/hives/${encodeURIComponent(name)}`),

  // `template_id` (#3037 Phase 3) selects a saved/persisted template from
  // the `/admin/templates` store (Phase 2 / #3036). When set, the backend
  // fetches the template and writes its `agents_md` into the new hive's
  // AGENTS.md at creation time. Mutually informative with `template` (the
  // built-in slug) — if both are supplied the backend treats `template_id`
  // as authoritative, so we still send `template` for legacy analytics /
  // category tagging.
  create: (
    name: string,
    template?: string,
    selected_agents?: string[],
    description?: string,
    template_id?: string,
  ): Promise<UserProject> =>
    adminFetch("POST", "/admin/hives", {
      name,
      ...(template            ? { template }                    : {}),
      ...(selected_agents?.length ? { selected_agents }        : {}),
      ...(description         ? { description }                 : {}),  // #1998 (backend #1997 stores it)
      ...(template_id         ? { template_id }                 : {}),  // #3037
    }),

  // #3274-A — pre-create availability probe used by the New Hive form to
  // show "Taken" inline before submit. We deliberately reuse the existing
  // `GET /admin/hives/{name}` route (the same one used by `get` above)
  // instead of adding a new `/check-availability` endpoint: the backend
  // already returns 200 when the hive exists for the caller and 404 when
  // it doesn't, which is exactly the answer we need. This keeps the
  // backend contract untouched (per #3274-A constraint) and avoids a
  // second auth-shaped surface to police.
  //
  // Returns:
  //   { available: true  } — slug is free for this caller
  //   { available: false } — slug already exists (caller owns it OR a
  //                          collision in the global namespace surfaces as
  //                          a 409 at create time; either way the user
  //                          should pick a different name)
  // Any other status (network, 5xx, auth) is rethrown so the caller can
  // distinguish "couldn't check" from "definitely taken".
  checkAvailability: async (name: string): Promise<{ available: boolean }> => {
    try {
      await adminFetch("GET", `/admin/hives/${encodeURIComponent(name)}`);
      return { available: false };
    } catch (ex) {
      if (ex instanceof ApiError && ex.status === 404) return { available: true };
      throw ex;
    }
  },

  remove: (name: string, opts?: ProjectDeleteOptions): Promise<void> =>
    adminFetch("DELETE", `/admin/hives/${encodeURIComponent(name)}`, opts ?? {}),

  // Re-enable a hive that was deleted with keep_github (status=disabled). (#1564)
  reenable: (name: string): Promise<UserProject> =>
    adminFetch("POST", `/admin/hives/${encodeURIComponent(name)}/reenable`),

  // Self-serve (re)provision a hive that is unprovisioned/clone_failed. (#1660)
  provision: (name: string): Promise<UserProject> =>
    adminFetch("POST", `/admin/hives/${encodeURIComponent(name)}/provision`),

  // #3005 Phase 1 — owner-only URL-seeded company brief. Backend validates
  // the URL list (1-5 HTTPS public-web URLs) and enqueues a fleet-agent
  // hive_seed job. Returns 202 with the job id; progress streams over the
  // fleet WS as seed_started / seed_crawling / seed_synthesizing /
  // seed_complete frames (see `useHiveSeed`).
  //
  // #3035 Phase 1.5 — payload widened to the dual-input shape:
  //   { description?: string,   // free-text "Describe this hive"
  //     urls?:        string[]  // 0-5 optional URL hints
  //   }
  // Backend accepts either field alone or both together (back-compat with
  // the URLs-only Phase 1 path). The supervisor verb decides what to
  // research from the description, merges the URL hints, and discovers
  // more URLs via DuckDuckGo HTML (keyless). The endpoint URL keeps its
  // legacy name `/seed-from-urls` so old clients still land in the right
  // handler — only the body shape grew.
  seedFromUrls: (
    name: string,
    urls: string[],
    description?: string,
  ): Promise<{
    status: "queued";
    job_id: string;
    hive_name: string;
    urls: string[];
    description?: string;
  }> =>
    adminFetch(
      "POST",
      `/admin/hives/${encodeURIComponent(name)}/seed-from-urls`,
      {
        ...(urls.length         ? { urls }                       : {}),
        ...(description         ? { description }                : {}),
      },
    ),

  // Admin all-hives view — every DDB row, platform-admin only (#1448 + #1447).
  // owner_email + org + template are joined server-side from 2hives-users.
  listAll: (): Promise<AdminHiveRow[]> =>
    adminFetch("GET", "/admin/hives?scope=all"),

  // Sync GitHub orphans — one-shot admin sweep that links 2hives-ai/* repos
  // without a DDB row to a chosen owner_sub (defaults to info@2hives.ai). (#1446)
  syncGithubOrphans: (ownerSub?: string): Promise<SyncOrphansResult> =>
    adminFetch("POST",
      `/admin/hives/sync-github-orphans${ownerSub ? `?owner_sub=${encodeURIComponent(ownerSub)}` : ""}`),

  // ── Directories (#1324) ────────────────────────────────────────────────────

  listDirs: (name: string): Promise<ProjectDirectory[]> =>
    adminFetch<{ directories: ProjectDirectory[] }>("GET", `/admin/hives/${encodeURIComponent(name)}/directories`)
      .then((r) => r.directories ?? []),

  addDir: (name: string, path: string, displayName?: string): Promise<ProjectDirectory> =>
    adminFetch("POST", `/admin/hives/${encodeURIComponent(name)}/directories`, {
      path, display_name: displayName ?? path,
    }),

  removeDir: (name: string, dirPath: string): Promise<void> =>
    adminFetch("DELETE", `/admin/hives/${encodeURIComponent(name)}/directories/${encodeURIComponent(dirPath)}`),

  getReadme: (name: string, dirPath: string): Promise<string> =>
    adminFetch<{ content: string }>("GET",
      `/admin/hives/${encodeURIComponent(name)}/directories/${encodeURIComponent(dirPath)}/readme`)
      .then((r) => r.content ?? ""),

  saveReadme: (name: string, dirPath: string, content: string): Promise<void> =>
    adminFetch("PATCH",
      `/admin/hives/${encodeURIComponent(name)}/directories/${encodeURIComponent(dirPath)}/readme`,
      { content }),

  // ── Filesystem + arbitrary file read/write (#1645/#1646) ───────────────────

  // List files+subdirs in a directory of the hive (path relative to hive root).
  // Backend returns the box verb result JSON; entries may be under `entries` or
  // `files`, or a bare array — tolerate all (#1645).
  listFilesystem: (name: string, path = "."): Promise<FsEntry[]> =>
    adminFetch<{ entries?: FsEntry[]; files?: FsEntry[] } | FsEntry[]>("GET",
      `/admin/hives/${encodeURIComponent(name)}/filesystem?path=${encodeURIComponent(path)}`)
      .then((r) => (Array.isArray(r) ? r : r.entries ?? r.files ?? [])),

  // Read a text file's content -> {content, size, truncated}; binary/oversize flagged.
  getFile: (name: string, path: string): Promise<HiveFile> =>
    adminFetch("GET",
      `/admin/hives/${encodeURIComponent(name)}/files?path=${encodeURIComponent(path)}`),

  // Write a text file's content -> {written, size}.
  putFile: (name: string, path: string, content: string): Promise<{ written?: boolean; size?: number }> =>
    adminFetch("PUT",
      `/admin/hives/${encodeURIComponent(name)}/files?path=${encodeURIComponent(path)}`,
      { content }),

  // ── Agents (#1324) ─────────────────────────────────────────────────────────

  // #2635 — backend (`functions/admin/project_agents.handle_list_agents`)
  // returns a bare JSON array of DDB rows whose keys (`agent_id`/`directory`)
  // + status vocabulary (`alive`/`down`/`quarantined`/…) don't match the
  // frontend `ProjectAgent` contract (`id`/`dir`, `running`/`stopped`/…).
  // Pre-fix: `r.agents ?? []` silently swallowed every response → chat panel
  // showed "No agents yet" for every hive that had agents (the P0 prod bug).
  // We now: (1) accept BOTH shapes (bare array + the long-planned `{agents}`
  // envelope) so a later backend change is non-breaking, and (2) normalize
  // each row to the typed contract so quarantined rows surface with their
  // real status — picked up by ChatPanel's empty-state affordance (#2621).
  listAgents: (name: string): Promise<ProjectAgent[]> =>
    adminFetch<ProjectAgent[] | { agents: ProjectAgent[] } | Array<Record<string, unknown>>>(
      "GET", `/admin/hives/${encodeURIComponent(name)}/agents`,
    ).then(normalizeAgentList),

  addAgent: (name: string, body: { dir: string; name: string; model?: string; role?: string }): Promise<ProjectAgent> =>
    adminFetch("POST", `/admin/hives/${encodeURIComponent(name)}/agents`, body),

  removeAgent: (name: string, agentId: string): Promise<void> =>
    adminFetch("DELETE", `/admin/hives/${encodeURIComponent(name)}/agents/${encodeURIComponent(agentId)}`),

  // #3151 — flip an agent's public/private visibility flag.
  // The backend writes the new value on the 2hives-project-agents row + an
  // audit row in platform_audit_log. Returns the (old, new) pair so the UI
  // can render an optimistic confirmation. Owner-only on the server side.
  setAgentVisibility: (
    name: string,
    agentId: string,
    visibility: "public" | "private",
  ): Promise<{
    agent_id:       string;
    hive_slug:      string;
    old_visibility: "public" | "private";
    new_visibility: "public" | "private";
    is_master:      boolean;
    ts:             string;
  }> =>
    adminFetch(
      "POST",
      `/admin/hives/${encodeURIComponent(name)}/agents/${encodeURIComponent(agentId)}/visibility`,
      { visibility },
    ),

  // #3197 — set the per-agent MCP tool allowlist (list of fnmatch globs).
  // Backend writes `allowed_tools` on the DDB row + an audit row. The
  // 200 response carries the (old, new) pair so the UI can render an
  // optimistic confirmation; if the resulting state is (public, ['*'])
  // the response also carries `X-Allowlist-Public-Warning: true`. Owner-
  // only on the server side.
  setAgentAllowlist: (
    name: string,
    agentId: string,
    allowed_tools: string[],
  ): Promise<{
    agent_id:   string;
    hive_slug:  string;
    old_list:   string[];
    new_list:   string[];
    is_master:  boolean;
    visibility: "public" | "private";
    ts:         string;
  }> =>
    adminFetch(
      "POST",
      `/admin/hives/${encodeURIComponent(name)}/agents/${encodeURIComponent(agentId)}/allowlist`,
      { allowed_tools },
    ),

  // ── Membership (#1358) ─────────────────────────────────────────────────────

  listMembers: (name: string): Promise<ProjectMember[]> =>
    adminFetch<{ members: ProjectMember[] }>("GET", `/admin/hives/${encodeURIComponent(name)}/members`)
      .then((r) => r.members ?? []),

  // #2987 — POST /admin/hives/{name}/invite is the actual deployed route on
  // the admin REST API (2hives-admin-api / 99xf040rkb). The previous call to
  // POST /admin/hives/{name}/members hit a path where only GET/DELETE were
  // declared, so API GW returned its default IAM_AUTH rejection
  //   "Invalid key=value pair (missing equal-sign) in Authorization header
  //    (hashed with SHA-256 and encoded with Base64)…"
  // — the same misleading-signature class as #2858. The /invite handler
  // returns `{status: "added"|"email_sent"|"pending_owner_approval", …}`
  // rather than a member envelope; the caller refreshes via listMembers().
  inviteMember: (
    name: string,
    email: string,
  ): Promise<{ status: string; detail?: string }> =>
    adminFetch<{ status: string; detail?: string }>(
      "POST",
      `/admin/hives/${encodeURIComponent(name)}/invite`,
      { email },
    ),

  removeMember: (name: string, userId: string): Promise<void> =>
    adminFetch("DELETE", `/admin/hives/${encodeURIComponent(name)}/members/${encodeURIComponent(userId)}`),

  leaveProject: (name: string): Promise<void> =>
    adminFetch("DELETE", `/admin/hives/${encodeURIComponent(name)}/members/me`),

  transferProject: (name: string, newOwnerUserId: string): Promise<void> =>
    adminFetch("POST", `/admin/hives/${encodeURIComponent(name)}/transfer`, { new_owner_user_id: newOwnerUserId }),
};
